import { useEffect, useState, type ChangeEvent } from "react";
import {
  bindOracleCandidate,
  buildWorkflowIntakeView,
  type IntakeCompileOutcome,
  type IntakeFailClosedReason,
  type WorkflowIntakeView,
} from "@excellent/core/verification-workflow-intake";
import type { VerificationPageProps } from "./page-frame";
import { PageFrame } from "./page-frame";
import { BIND_PREREQ, ACTION, Btn, workflowHref } from "./workflows-contracts";
import { useWorkflowActions } from "./workflows-actions";
import { IntakeResult } from "./workflow-intake-result";
import { FAIL_CLOSED_CHOICES, SAMPLE_DOC, standInCompile } from "./workflow-compile";
import { CodingIntakePanel } from "./coding-intake-panel";
import { useCanonicalEnvironmentOptional } from "../canonical-environment-provider";
import { canonicalWorkReadQuery } from "./work/environment-read-scope";

const REPOSITORY_CONTEXT_SIGNALS = [
  "Repository shape, workspace globs, package managers, languages, build systems, CI workflows, deployment manifests, instruction files, generated-code boundaries, and unsupported-size boundaries are shown as execution-context facts.",
  "Candidate commands expose their origin path, origin digest, working directory, environment needs, expected artifacts, and confidence before they can become verification obligations.",
  "Changed-path narrowing may select nearer commands, but cross-cutting safety gates stay preserved and unrelated Excellent-internal defaults are not injected into customer repositories.",
  "Frozen package, gate, CI, and instruction definitions are compared by digest so stale or worker-modified execution context cannot silently authorize a receipt.",
];

export function WorkflowNewPage({
  onNavigate,
  initialIntent,
}: VerificationPageProps & { initialIntent?: string }) {
  const { environment, createWorkflow, admitWorkflow } = useWorkflowActions();
  const canonicalEnvironment = useCanonicalEnvironmentOptional();
  const environmentQuery = canonicalWorkReadQuery(canonicalEnvironment);
  const [doc, setDoc] = useState("");
  const [oracle, setOracle] = useState("");
  const [failMode, setFailMode] = useState<"" | IntakeFailClosedReason>("");
  const [submitted, setSubmitted] = useState<WorkflowIntakeView | null>(null);
  const [bindError, setBindError] = useState<string | null>(null);
  const [persistError, setPersistError] = useState<string | null>(null);
  const canUseStandInCompile = environment === "demo";

  useEffect(() => {
    // An intent carried on the URL is an explicit request and outranks both the
    // blank reset and the demo sample; absent one, main's behaviour is unchanged.
    const intent = initialIntent?.trim();
    if (!canUseStandInCompile) {
      setDoc(intent ?? "");
      setSubmitted(null);
      return;
    }
    setDoc((current) => current || intent || SAMPLE_DOC);
  }, [canUseStandInCompile, initialIntent]);

  if (environment === "real") {
    return (
      <PageFrame
        page="workflow-new"
        eyebrow="Define"
        title="Define a coding task"
        lede="Give your agent a clear task, observable outcomes, and an agreed scope."
      >
        {environmentQuery ? (
          <CodingIntakePanel
            onNavigate={onNavigate}
            initialIntent={initialIntent}
            environmentQuery={environmentQuery}
            environmentLocality={
              canonicalEnvironment?.response?.presentation?.locality.kind ?? "unknown"
            }
          />
        ) : (
          <div className="vp-callout" data-tone="unknown" role="status">
            <div className="vp-callout-title">Verified Local Environment required</div>
            <div className="vp-callout-body">
              Select a verified Local Environment to review and accept this coding task. Its exact
              Environment and generation must be available before submission.
            </div>
          </div>
        )}
      </PageFrame>
    );
  }

  const run = () => {
    setBindError(null);
    setPersistError(null);
    if (!canUseStandInCompile) {
      setSubmitted(null);
      setPersistError(BIND_PREREQ);
      return;
    }
    const outcome: IntakeCompileOutcome = failMode
      ? { state: "fail-closed", reason: failMode, detail: "simulated for review" }
      : standInCompile(doc);
    setSubmitted(
      buildWorkflowIntakeView({ doc, name: "Pasted workflow", outcome, boundOracle: oracle }),
    );
  };

  const bind = (key: string) => {
    if (!submitted) return;
    const result = bindOracleCandidate({ view: submitted, candidateKey: key });
    if (result.ok) {
      setSubmitted(result.view);
      setOracle(key);
      setBindError(null);
      return;
    }
    setBindError(result.detail);
  };

  const persistAdmitted = async () => {
    if (!submitted || submitted.admission.status !== "admit") return;
    if (!environment) {
      setPersistError("The active Environment must load before creating a workflow.");
      return;
    }
    const bound =
      oracle.trim() || submitted.oracleCandidates[0]?.key || "git-tree object under review";
    const created = await createWorkflow({
      name: submitted.name,
      actor: "intake",
      oracle: bound,
      doc,
      evidence: bound,
    });
    if (!created.ok || !created.createdId) {
      setPersistError(
        created.ok ? "Create did not return an id." : `Cannot create (${created.reason}).`,
      );
      return;
    }
    if (created.workflow.status === "admitted") {
      onNavigate(workflowHref(created.createdId, environment));
      return;
    }
    const admitted = await admitWorkflow(created.createdId);
    if (!admitted.ok) {
      setPersistError(`Created but not admitted (${admitted.reason}).`);
      onNavigate(workflowHref(created.createdId, environment));
      return;
    }
    onNavigate(workflowHref(created.createdId, environment));
  };

  return (
    <PageFrame
      page="workflow-new"
      eyebrow="Define"
      title="Connect a workflow"
      lede="Paste what the workflow is supposed to guarantee. The engine derives the mechanical predicates and tells you whether it can be admitted."
    >
      <div className="vp-callout" data-tone="accent">
        <div className="vp-callout-title">What is real on this page</div>
        <div className="vp-callout-body">
          Admission, grounding, the oracle catalog, and every refusal are shipped engine code. The
          compile step is a declared stand-in: a browser cannot reach the in-boundary local model;
          nothing is admitted until an independent oracle is named.
        </div>
      </div>
      <div className="vp-callout" data-tone="unknown">
        <div className="vp-callout-title">Source-backed intent only</div>
        <div className="vp-callout-body">
          Intent is derived only from retained source evidence: repository policy wins over
          requester issue text, and requester work objects win over worker proposals. Every inferred
          outcome keeps its source span and digest. Vague requests like “production ready” return to
          confirmation instead of inventing availability, performance, or durability criteria.
          Permission-limited or revoked observations do not infer hidden outcomes, and the workflow
          intake will not broad-scan outside the granted resources.
        </div>
      </div>
      <RepositoryExecutionContextPanel />
      <div className="vp-card">
        <div className="vp-card-body">
          <label className="vp-label" htmlFor="wf-intake-doc">
            Workflow description
          </label>
          <textarea
            id="wf-intake-doc"
            className="vp-textarea"
            data-action-id={ACTION.intakeDoc}
            value={doc}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDoc(e.target.value)}
            placeholder="Describe what the workflow guarantees..."
            disabled={!canUseStandInCompile}
          />
          <label className="vp-label mt-3" htmlFor="wf-intake-oracle">
            Independent oracle (optional)
          </label>
          <input
            id="wf-intake-oracle"
            className="vp-input"
            data-action-id={ACTION.intakeOracle}
            value={oracle}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setOracle(e.target.value)}
            placeholder="e.g. the payment processor"
            disabled={!canUseStandInCompile}
          />
          <div className="vp-label mt-3">Compile environment</div>
          <div className="flex flex-wrap items-center gap-2">
            {FAIL_CLOSED_CHOICES.map((choice) => (
              <Btn
                key={choice.value || "ok"}
                actionId={ACTION.failMode}
                variant={failMode === choice.value ? "primary" : "outline"}
                size="sm"
                disabled={!canUseStandInCompile}
                onClick={() => setFailMode(choice.value)}
              >
                {choice.label}
              </Btn>
            ))}
          </div>
          <div className="mt-4">
            <Btn
              actionId={ACTION.derive}
              variant="primary"
              disabled={!canUseStandInCompile}
              title={canUseStandInCompile ? "Derive predicates" : BIND_PREREQ}
              onClick={run}
            >
              Derive predicates
            </Btn>
          </div>
        </div>
      </div>
      {submitted ? (
        <IntakeResult
          view={submitted}
          bindError={bindError}
          persistError={persistError}
          onBind={bind}
          onPersist={persistAdmitted}
          onNavigate={onNavigate}
          canMutate={Boolean(environment)}
        />
      ) : null}
    </PageFrame>
  );
}

function RepositoryExecutionContextPanel() {
  return (
    <div
      className="vp-card"
      data-proof-id="cu105-repository-execution-context"
      data-cu105-repository-execution-context="capability-map"
    >
      <div className="vp-card-head">
        <div className="vp-card-title">Repository execution context</div>
      </div>
      <div className="vp-card-body">
        <div className="vp-callout" data-tone="unknown">
          <div className="vp-callout-title">Capability map drives routing, not verdicts</div>
          <div className="vp-callout-body">
            The repository map is the product-visible execution-context input for admission and
            receipt review. It explains what the system can inspect, which commands are eligible,
            which safety gates remain mandatory, and why unsupported or stale contexts stay
            UNKNOWN/NOT_RUN instead of becoming PASS.
          </div>
        </div>
        <ul>
          {REPOSITORY_CONTEXT_SIGNALS.map((signal, index) => (
            <li key={signal} data-cu105-repository-signal={String(index + 1)}>
              {signal}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
